import { Text, useMantineTheme } from "@mantine/core";
import { useModals } from "@mantine/modals";
import { useCallback, memo, useState } from "react";
import ActionCard from "src/components/ActionCard";
import ListActions, {
  IActiveOption,
  IListObj,
} from "src/components/ListActions";
import {
  GetMeetingQuery,
  useAddAttendeeToMeetingMutation,
  useCancelAttendeeToMeetingMutation,
  usePullFromMeetingMutation,
  useUpdateAttendanceMutation,
  AttendanceStatus,
} from "src/generated/graphql";
import { SearchList } from "../SearchList";
import { GetMeeting } from "src/api/graphql/queries/Meeting";
import { MeetingTypes } from "src/utils/constants";

type IAttendees = {
  meetingDetail: GetMeetingQuery["meetingOne"];
  isAllowWrite: boolean;
};

function Attendees({ meetingDetail, isAllowWrite }: IAttendees) {
  const {
    attendances,
    waitlistees,
    maxOccupancy,
    waitlistMax,
    isWaitlistEnabled,
  } = meetingDetail || {};
  const modalId = "search-accounts";
  const modals = useModals();
  const theme = useMantineTheme();
  let attendeesLen = attendances?.length;
  const [activeOption, setActiveOption] = useState<IActiveOption>(undefined);
  const refetchQuery = [
    { query: GetMeeting, variables: { id: meetingDetail!.id } },
  ];

  const [noShowLoading, setNoShowLoading] = useState<boolean>(false);
  const [checkInLoading, setCheckInLoading] = useState<boolean>(false);

  const [moveToWaitlistee, { loading: moveLoading }] =
    usePullFromMeetingMutation({
      refetchQueries: refetchQuery,
    });

  const [addAttendee] = useAddAttendeeToMeetingMutation({
    onCompleted() {
      modals.closeModal(modalId);
    },
    refetchQueries: refetchQuery,
  });

  const [updateAttendee] = useUpdateAttendanceMutation({
    onCompleted() {
      setActiveOption(undefined);
      setCheckInLoading(false);
      setNoShowLoading(false);
    },
    refetchQueries: refetchQuery,
  });

  const [cancelAttendee, { loading: deleteLoading }] =
    useCancelAttendeeToMeetingMutation({
      refetchQueries: refetchQuery,
    });

  const handleSubmit = (id: string) => {
    meetingDetail &&
      addAttendee({
        variables: { meetingId: meetingDetail.id, attendeeId: id },
      });
  };

  const handleModal = useCallback(() => {
    let _ids = attendances?.map((e) => e.attendee.id) || [];
    modals.openModal({
      closeOnClickOutside: false,
      modalId,
      title: "Add Attendee",
      children: (
        <SearchList
          type={MeetingTypes.Attendee}
          onCancel={() => modals.closeModal(modalId)}
          onSubmit={(e) => handleSubmit(e)}
          currentIdList={_ids}
          waitlisteeClients={waitlistees?.map((e) => e.client.id) || []}
        />
      ),
    });
  }, [meetingDetail, modalId]);

  const onMoveToWaitList = ({ clientId }: IListObj) => {
    clientId &&
      meetingDetail &&
      moveToWaitlistee({
        variables: { meetingId: meetingDetail.id, clientId },
      }).then(() => setActiveOption(undefined));
  };

  const updateAttendeeStatus = ({ id }: IListObj, status: AttendanceStatus) => {
    const data = {
      attendanceStatus: status,
    };
    if (status === AttendanceStatus.Attended) {
      setCheckInLoading(true);
    } else if (status === AttendanceStatus.Missed) {
      setNoShowLoading(true);
    }

    updateAttendee({
      variables: { attendeeId: id, meetingId: meetingDetail?.id!, data },
    });
  };

  const minutesInDay = 24 * 60;
  const miliSeconds = 1000;
  const minutesInHour = 60;
  const currentTime = new Date().getTime();
  const meetingStartTime = new Date(meetingDetail?.startAt).getTime();
  const meetingEndTime = new Date(meetingDetail?.endAt).getTime();
  const isDayPastDiff =
    new Date(currentTime) > new Date(meetingEndTime) &&
    Math.floor((currentTime - meetingEndTime) / miliSeconds / minutesInHour) >
      minutesInDay;

  const isDayDiff =
    Math.floor((meetingStartTime - currentTime) / miliSeconds / minutesInHour) <
    minutesInDay;
  const isHalfHourDiff =
    Math.floor(
      (meetingStartTime - currentTime) / miliSeconds / minutesInHour
    ) <= 30 &&
    Math.floor(
      (meetingStartTime - currentTime) / miliSeconds / minutesInHour
    ) >= 0;
  const isMeetingStarted = currentTime > meetingStartTime;

  const onDelete = ({ id, name, isPaid }: IListObj) => {
    if (isDayDiff && isPaid) {
      modals.openConfirmModal({
        closeOnClickOutside: false,
        title: "Confirm Cancel",
        children: <Text size="sm">Do you want your fee to be waived ?</Text>,
        labels: { confirm: "Confirm", cancel: "No" },
        onCancel: () => {
          updateAttendee({
            variables: {
              attendeeId: id,
              meetingId: meetingDetail?.id!,
              feeWaived: false,
              data: { attendanceStatus: AttendanceStatus.Cancelled },
            },
          });
        },
        onConfirm: () => {
          updateAttendee({
            variables: {
              attendeeId: id,
              meetingId: meetingDetail?.id!,
              feeWaived: true,
              data: { attendanceStatus: AttendanceStatus.Cancelled },
            },
          });
        },
      });
    } else {
      modals.openConfirmModal({
        closeOnClickOutside: false,
        title: "Confirm Cancel",
        children: <Text size="sm">Do you want to Cancel ?</Text>,
        labels: { confirm: "Confirm", cancel: "No" },
        onCancel: () => modals.closeAll(),
        onConfirm: () => {
          cancelAttendee({
            variables: { attendeeId: id, meetingId: meetingDetail!.id },
          });
        },
      });
    }
  };

  return (
    <ActionCard
      title={`Attendees - ${attendeesLen}/${maxOccupancy} 
      ${attendeesLen === maxOccupancy ? "- Full" : ""}`}
      handleModal={isAllowWrite ? handleModal : undefined}
      disabled={attendeesLen === maxOccupancy || isDayPastDiff}
      hideChild={!attendances?.length}
    >
      <>
        <ListActions
          options={
            attendances?.map((e) => ({
              id: e.attendee.id,
              name: `${e.attendee.client.user.firstName} ${e.attendee.client.user.lastName}`,
              clientId: e.attendee.client?.id,
              checkForClient: true,
              attendanceStatus: e.attendanceStatus,
              isPaid: e.isPaid,
            })) || []
          }
          showCheckIn={isHalfHourDiff || isMeetingStarted}
          showNoShow={isMeetingStarted}
          showEdit={
            isDayDiff &&
            (waitlistees?.length === waitlistMax || isWaitlistEnabled)
          }
          showCancel={!isMeetingStarted}
          isAttendee={true}
          isWaitlistEnabled={isWaitlistEnabled}
          CheckInLoading={checkInLoading}
          noShowLoading={noShowLoading}
          waitlistLoading={moveLoading}
          cancelLoading={deleteLoading}
          onUpdateWaitList={onMoveToWaitList}
          onUpdateAttendanceStatus={updateAttendeeStatus}
          onCancel={onDelete}
          isAllowWrite={isAllowWrite}
          activeOption={activeOption}
          setActiveOption={setActiveOption}
        />
      </>
    </ActionCard>
  );
}

export default memo(Attendees);
